import { useMemo, useState } from 'react';
import P from 'prop-types';
import { useStringsContext } from '../../../contexts/strings';
import Header from '../../common/team/header';
import { Box, Tabs, Tab } from '@mui/material';
import ScenePage from '../../common/scene/scene-page';
import { FotbollPlayProps } from './types';
import { MediaTab } from './tabs/MediaTab';
import { VideoCarouselTab } from './tabs/VideoCarouselTab';
import { StartTab } from './tabs/StartTab';
import { NotificationTab } from './tabs/NotificationTab';
import { VideoCarouselsProvider } from './hooks/useVideoCarousels';
import { FotbollPlaySubmitProvider, useFotbollPlaySubmit } from './api/FotbollPlaySubmitProvider';
import { useFotbollPlayConfig } from './api/useFotbollPlayApi';
import {
  DEFAULT_STANDARD_CATEGORY_CLIP_COUNT,
  type FotbollPlayAppConfig,
  type FotbollPlayTabIndex,
} from './api/types';
import Loading from '../../loading';
import { useUserAuth } from '../../../contexts/auth';
import { ROLES } from '../../../types';

function FotbollPlayContent({
  teamAbbrv,
  config,
}: {
  teamAbbrv: string;
  config?: FotbollPlayAppConfig;
}) {
  const [activeTab, setActiveTab] = useState(0);
  const { isSubmitting, submitActiveTab } = useFotbollPlaySubmit();

  const { isSuperAdmin, hasTeamRole } = useUserAuth();
  const isReadOnly = !isSuperAdmin && hasTeamRole(teamAbbrv, ROLES.VIEWER);

  function handleSubmit() {
    void submitActiveTab(activeTab as FotbollPlayTabIndex);
  }

  return (
    <>
      <Header
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        isDisabled={isSubmitting}
        title="Play"
        isReadOnly={isReadOnly}
        border
      />

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
        <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
          <Tab label="Inställningar" />
          <Tab label="Start" />
          <Tab label="Videokaruseller" />
          {/* <Tab label="Notiser och djuplänkar" /> */}
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <MediaTab
          teamAbbrv={teamAbbrv}
          config={
            config?.settings ?? {
              playLogo: '',
              showLogoOnThumbnails: false,
              showOtherTeamsMatchClips: false,
              standardCategoryClipCount: DEFAULT_STANDARD_CATEGORY_CLIP_COUNT,
            }
          }
        />
      )}
      {activeTab === 1 && <StartTab teamAbbrv={teamAbbrv} config={config?.start} />}
      {activeTab === 2 && <VideoCarouselTab />}
      {activeTab === 3 && <NotificationTab teamAbbrv={teamAbbrv} />}
    </>
  );
}

function FotbollPlay({ match }: FotbollPlayProps) {
  const teamAbbrv = match.params.id;
  const { strings } = useStringsContext();
  const [config, loading, error] = useFotbollPlayConfig(teamAbbrv);
  const initialCarouselsKey = JSON.stringify(config?.videoCarousels?.carousels ?? []);
  const initialCarousels = useMemo(() => JSON.parse(initialCarouselsKey), [initialCarouselsKey]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <p>{error.message}</p>;
  }

  return (
    <ScenePage title={strings.appBar.teamData}>
      <VideoCarouselsProvider teamAbbrv={teamAbbrv} initialCarousels={initialCarousels}>
        <FotbollPlaySubmitProvider teamAbbrv={teamAbbrv}>
          <FotbollPlayContent teamAbbrv={teamAbbrv} config={config} />
        </FotbollPlaySubmitProvider>
      </VideoCarouselsProvider>
    </ScenePage>
  );
}

FotbollPlay.propTypes = {
  match: P.shape({
    params: P.shape({
      id: P.string.isRequired,
    }).isRequired,
  }).isRequired,
};

export default FotbollPlay;
